import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import Swal from 'sweetalert2';

import { obtenerListaEmpresas } from '../../../../store/Empresa/thunkEmpresa';

// Cambiar importación de crearAgenda a actualizarAgenda
import { actualizarAgenda, extraerEventoId } from '../../../../store/agenda/thunkAgenda';
import { traerTodoslosClientesSegunlaempresa } from '../../../../store/clientes/thunkClientes';


export const EditarAgenda = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Obtener el ID del evento de la URL
    const eventoId = searchParams.get('data');

    // Estado para el formulario
    const [formData, setFormData] = useState({
        nombre_agenda: '',
        tipo_agenda: '',
        color_agenda: '',
        fecha_inicio_agenda: '',
        hora_inicio_agenda: '',
        fecha_fin_agenda: '',
        hora_fin_agenda: '',
        estado_agenda: 'pendiente',
        ubicacion_agenda: '', // Añadido nuevo campo para ubicación
        descripcion_agenda: '',
        id_cliente: '',
        nombre_cliente: '',
        correo_cliente: '',
        telefono_cliente: '',
        id_empresa: ''
    });

    // Funciones auxiliares para formatear fechas y horas
    const formatearFecha = (fechaISO) => {
        if (!fechaISO) return '';
        const fecha = new Date(fechaISO);
        return fecha.toISOString().split('T')[0]; // Formato YYYY-MM-DD
    };

    const formatearHora = (horaString) => {
        if (!horaString) return '';
        return horaString.substring(0, 5); // Formato HH:MM
    };

    // Cargar datos del evento al iniciar
    useEffect(() => {
        const cargarDatosEvento = async () => {
            try {
                if (eventoId) {
                    // Mostrar loader mientras carga
                    Swal.fire({
                        title: "Cargando datos...",
                        text: "Por favor, espere un momento",
                        icon: "info",
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        didOpen: () => {
                            Swal.showLoading();
                        },
                    });

                    // Obtener datos del evento por su ID
                    const eventoData = await dispatch(extraerEventoId(eventoId));

                    // Si obtenemos datos, actualizar el formulario
                    if (eventoData) {
                        const evento = eventoData.data.data[0];
                        setFormData({
                            nombre_agenda: evento.nombre_agenda || '',
                            tipo_agenda: evento.tipo_agenda || '',
                            color_agenda: evento.color_agenda || '',
                            fecha_inicio_agenda: formatearFecha(evento.fecha_inicio_agenda),
                            hora_inicio_agenda: formatearHora(evento.hora_inicio_agenda),
                            fecha_fin_agenda: formatearFecha(evento.fecha_fin_agenda),
                            hora_fin_agenda: formatearHora(evento.hora_fin_agenda),
                            estado_agenda: evento.estado_agenda || 'pendiente',
                            ubicacion_agenda: evento.ubicacion_agenda || '', // Carga la ubicación del evento
                            descripcion_agenda: evento.descripcion_agenda || '',
                            id_cliente: evento.id_cliente_agenda || '',
                            nombre_cliente: '', // Se completará si hay datos de cliente
                            correo_cliente: '',
                            telefono_cliente: '',
                            id_empresa: evento.id_empresa_agenda || ''
                        });

                        // Cargar clientes de la empresa si hay ID de empresa
                        if (evento.id_empresa_agenda) {
                            dispatch(traerTodoslosClientesSegunlaempresa(evento.id_empresa_agenda))
                                .then((clientesData) => {
                                    setClientesData(clientesData);

                                    const options = clientesData.map(({ id_cliente, nombre_cliente }) => ({
                                        value: id_cliente,
                                        label: nombre_cliente,
                                    }));

                                    setClientesOpciones(options);

                                    // Si hay un cliente asociado, buscar sus datos
                                    if (evento.id_cliente_agenda) {
                                        const clienteSeleccionado = clientesData.find(
                                            cliente => cliente.id_cliente === evento.id_cliente_agenda
                                        );

                                        if (clienteSeleccionado) {
                                            setFormData(prevState => ({
                                                ...prevState,
                                                nombre_cliente: clienteSeleccionado.nombre_cliente || '',
                                                correo_cliente: clienteSeleccionado.correo_cliente || '',
                                                telefono_cliente: clienteSeleccionado.telefono_cliente || ''
                                            }));
                                        }
                                    }
                                })
                                .catch(error => {
                                    console.error("Error al cargar los clientes:", error);
                                });
                        }
                    }

                    Swal.close();
                } else {
                    // Si no hay ID, redirigir a la lista de agendas
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'No se especificó qué agenda editar'
                    });
                    navigate('/agenda/personal');
                }
            } catch (error) {
                console.error("Error al cargar datos del evento:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudieron cargar los datos de la agenda'
                });
            }
        };

        cargarDatosEvento();
    }, [dispatch, eventoId, navigate]);

    // Opciones predefinidas para el tipo de agenda con sus colores asignados
    const tiposAgenda = [
        { value: 'Inspección', label: 'Inspección', color: '#2980b9' }, // Añadido nuevo
        { value: 'Llamada', label: 'Llamada', color: '#e74c3c' },
        { value: 'Cita', label: 'Cita', color: '#9b59b6' },
        { value: 'Tarea', label: 'Tarea', color: '#eb984e' }
    ];

    // Opciones para el estado de la agenda
    const estadosAgenda = [
        { value: 'pendiente', label: 'Pendiente' },
        { value: 'cancelado', label: 'Cancelado' },
        { value: 'completado', label: 'Completado' }
    ];

    // Generamos opciones de horas cada 30 minutos
    const generarOpcionesHora = () => {
        const opciones = [];
        for (let hora = 7; hora <= 22; hora++) { // De 7:00 AM a 10:00 PM
            // Formato para hora en punto (7:00, 8:00)
            opciones.push({
                value: `${hora.toString().padStart(2, '0')}:00`,
                label: `${hora % 12 === 0 ? 12 : hora % 12}:00 ${hora < 12 ? 'AM' : 'PM'}`
            });
            // Formato para media hora (7:30, 8:30)
            opciones.push({
                value: `${hora.toString().padStart(2, '0')}:30`,
                label: `${hora % 12 === 0 ? 12 : hora % 12}:30 ${hora < 12 ? 'AM' : 'PM'}`
            });
        }
        return opciones;
    };

    const opcionesHora = generarOpcionesHora();

    // Estados para las opciones de selección
    const [empresasOpciones, setEmpresasOpciones] = useState([]);
    const [clientesOpciones, setClientesOpciones] = useState([]);
    const [clientesData, setClientesData] = useState([]);

    // Cargar empresas al iniciar
    useEffect(() => {
        const cargarEmpresas = async () => {
            try {
                const empresasData = await dispatch(obtenerListaEmpresas());
                const opcionesEmpresas = empresasData.map(({ id_empresa, nombre_empresa }) => ({
                    value: id_empresa,
                    label: nombre_empresa,
                }));
                setEmpresasOpciones(opcionesEmpresas);
            } catch (error) {
                console.error("Error al cargar empresas:", error);
            }
        };

        cargarEmpresas();
    }, [dispatch]);

    // Función para manejar el cambio de empresa y cargar sus clientes
    const handleEmpresaChange = async (selectedOption) => {
        const selected = selectedOption ? selectedOption.value : '';

        setFormData({
            ...formData,
            id_empresa: selected,
            // Limpiar datos del cliente cuando cambia la empresa
            id_cliente: '',
            nombre_cliente: '',
            correo_cliente: '',
            telefono_cliente: ''
        });

        if (selected) {
            try {
                const leadData = await dispatch(traerTodoslosClientesSegunlaempresa(selected));
                setClientesData(leadData);

                const options = leadData.map(({ id_cliente, nombre_cliente }) => ({
                    value: id_cliente,
                    label: nombre_cliente,
                }));

                setClientesOpciones(options);
            } catch (error) {
                console.error("Error al cargar clientes:", error);
                setClientesOpciones([]);
            }
        } else {
            setClientesOpciones([]);
            setClientesData([]);
        }
    };

    // Función para manejar el cambio de cliente
    const handleClienteChange = (selectedOption) => {
        const selectedClientId = selectedOption ? selectedOption.value : '';

        if (selectedClientId) {
            const clienteCompleto = clientesData.find(cliente =>
                cliente.id_cliente === selectedClientId
            );

            if (clienteCompleto) {
                setFormData({
                    ...formData,
                    id_cliente: clienteCompleto.id_cliente,
                    nombre_cliente: clienteCompleto.nombre_cliente,
                    correo_cliente: clienteCompleto.correo_cliente || '',
                    telefono_cliente: clienteCompleto.telefono_cliente || ''
                });
            }
        } else {
            setFormData({
                ...formData,
                id_cliente: '',
                nombre_cliente: '',
                correo_cliente: '',
                telefono_cliente: ''
            });
        }
    };

    // Función para manejar el cambio del tipo de agenda
    const handleTipoAgendaChange = (selectedOption) => {
        if (selectedOption) {
            // Asignar tipo y color correspondiente
            const tipoSeleccionado = tiposAgenda.find(tipo => tipo.value === selectedOption.value);
            setFormData({
                ...formData,
                tipo_agenda: tipoSeleccionado.value,
                color_agenda: tipoSeleccionado.color
            });
        } else {
            setFormData({
                ...formData,
                tipo_agenda: '',
                color_agenda: ''
            });
        }
    };

    // Función para manejar el cambio de estado de la agenda
    const handleEstadoChange = (selectedOption) => {
        setFormData({
            ...formData,
            estado_agenda: selectedOption ? selectedOption.value : 'pendiente'
        });
    };

    // Función para manejar el cambio de fecha de inicio
    const handleFechaInicioChange = (e) => {
        const fechaInicio = e.target.value;
        setFormData({
            ...formData,
            fecha_inicio_agenda: fechaInicio,
            fecha_fin_agenda: fechaInicio // Copiamos la fecha de inicio a la fecha fin
        });
    };

    // Función para manejar el cambio de hora de inicio
    const handleHoraInicioChange = (selectedOption) => {
        const horaInicio = selectedOption ? selectedOption.value : '';

        let horaFin = '';
        if (horaInicio) {
            // Calculamos una hora después
            const [horas, minutos] = horaInicio.split(':').map(Number);
            const horaFinal = horas + 1;
            horaFin = `${horaFinal.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}`;
        }

        setFormData({
            ...formData,
            hora_inicio_agenda: horaInicio,
            hora_fin_agenda: horaFin
        });
    };

    // Función para manejar el cambio de hora de fin
    const handleHoraFinChange = (selectedOption) => {
        setFormData({
            ...formData,
            hora_fin_agenda: selectedOption ? selectedOption.value : ''
        });
    };

    // Manejar cambios en los campos del formulario
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    // Manejar envío del formulario
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // Mostrar diálogo de confirmación antes de actualizar la agenda
            const confirmResult = await Swal.fire({
                icon: 'question',
                title: '¿Estás seguro?',
                text: '¿Deseas guardar los cambios en este evento?',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Sí, guardar',
                cancelButtonText: 'Cancelar'
            });

            // Solo continuar si el usuario confirma
            if (confirmResult.isConfirmed) {
                // Muestra un mensaje con un preloader indicando que el proceso está en curso.
                Swal.fire({
                    title: "Actualizando agenda...",
                    text: "Por favor, espere mientras se guardan los cambios.",
                    icon: "info",
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    didOpen: () => {
                        Swal.showLoading();
                    },
                });

                // Llamar a la función para actualizar la agenda
                // Asegurar que se envía el ID del evento
                const dataToUpdate = {
                    ...formData,
                    id_agenda: eventoId // Asegurarse de que se incluye el ID
                };

                await dispatch(actualizarAgenda(dataToUpdate));

                // Mostrar alerta de éxito
                Swal.fire({
                    icon: 'success',
                    title: '¡Éxito!',
                    text: 'Se ha actualizado la agenda correctamente',
                    confirmButtonColor: '#3085d6'
                }).then((result) => {
                    if (result.isConfirmed) {
                        // Redirigir a la lista de agendas
                        navigate('/agenda/personal');
                    }
                });
            }
            // Si el usuario cancela, no se hace nada

        } catch (error) {
            console.error("Error al actualizar la agenda:", error);

            // Mostrar alerta de error
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo actualizar la agenda. Por favor, inténtelo de nuevo.'
            });
        }
    };

    // Estilos personalizados para los selectores
    const customStyles = {
        control: (provided) => ({
            ...provided,
            borderRadius: '0.375rem',
            borderColor: '#ced4da',
            minHeight: '38px',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#86b7fe'
            }
        }),
        option: (provided, state) => {
            // Si es un tipo de agenda con color, mostramos un indicador de color
            const isAgendaType = tiposAgenda.some(tipo => tipo.value === state.data.value);
            const tipoAgenda = tiposAgenda.find(tipo => tipo.value === state.data.value);

            return {
                ...provided,
                backgroundColor: state.isSelected ? '#007bff' :
                    state.isFocused ? '#f8f9fa' : 'white',
                ...(isAgendaType && {
                    borderLeft: `4px solid ${tipoAgenda.color}`
                })
            };
        }
    };

    return (
        <>
            <h2 className="card-title mb-4">Editar Agenda</h2>

            {/* Alerta de estado */}
            {formData.estado_agenda && (
                <div className={`alert alert-${formData.estado_agenda === 'pendiente' ? 'warning' :
                    formData.estado_agenda === 'completado' ? 'success' : 'danger'
                    } mb-4`} role="alert">
                    <strong>Estado del evento: </strong>
                    {formData.estado_agenda === 'pendiente' ? 'Pendiente' :
                        formData.estado_agenda === 'completado' ? 'Completado' : 'Cancelado'}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="row">
                    {/* Columna izquierda */}
                    <div className="col-md-6">
                        {/* Nombre de agenda */}
                        <div className="mb-3">
                            <label className="form-label">Nombre de la Agenda*</label>
                            <input
                                type="text"
                                className="form-control"
                                name="nombre_agenda"
                                value={formData.nombre_agenda}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        {/* Fecha inicio */}
                        <div className="mb-3">
                            <label className="form-label">Fecha Inicio*</label>
                            <input
                                type="date"
                                className="form-control"
                                name="fecha_inicio_agenda"
                                value={formData.fecha_inicio_agenda}
                                onChange={handleFechaInicioChange}
                                required
                            />
                        </div>

                        {/* Fecha fin */}
                        <div className="mb-3">
                            <label className="form-label">Fecha Fin*</label>
                            <input
                                type="date"
                                className="form-control"
                                name="fecha_fin_agenda"
                                value={formData.fecha_fin_agenda}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                    </div>

                    {/* Columna derecha */}
                    <div className="col-md-6">
                        {/* Tipo de agenda */}
                        <div className="mb-3">
                            <label className="form-label">Tipo de Agenda*</label>
                            <Select
                                options={tiposAgenda}
                                onChange={handleTipoAgendaChange}
                                placeholder="Seleccione tipo de agenda"
                                isClearable
                                styles={customStyles}
                                className="basic-select"
                                classNamePrefix="select"
                                value={tiposAgenda.find(option => option.value === formData.tipo_agenda) || null}
                                required
                            />
                            {formData.tipo_agenda && (
                                <div className="mt-2 d-flex align-items-center">
                                    <div
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            backgroundColor: formData.color_agenda,
                                            borderRadius: '4px',
                                            marginRight: '8px',
                                            border: '1px solid #dee2e6'
                                        }}
                                    />
                                    <small className="text-muted">Color asignado para este tipo de agenda</small>
                                </div>
                            )}
                        </div>

                        {/* Estado del Evento */}
                        <div className="mb-3">
                            <label className="form-label">Estado del Evento*</label>
                            <Select
                                options={estadosAgenda}
                                onChange={handleEstadoChange}
                                placeholder="Seleccione el estado"
                                styles={customStyles}
                                className="basic-select"
                                classNamePrefix="select"
                                value={estadosAgenda.find(option => option.value === formData.estado_agenda) || null}
                                required
                            />
                        </div>

                        {/* Hora inicio */}
                        <div className="mb-3">
                            <label className="form-label">Hora Inicio*</label>
                            <Select
                                options={opcionesHora}
                                onChange={handleHoraInicioChange}
                                placeholder="Seleccione hora"
                                isClearable
                                styles={customStyles}
                                className="basic-select"
                                classNamePrefix="select"
                                value={opcionesHora.find(option => option.value === formData.hora_inicio_agenda) || null}
                                required
                            />
                        </div>

                        {/* Hora fin */}
                        <div className="mb-3">
                            <label className="form-label">Hora Fin*</label>
                            <Select
                                options={opcionesHora}
                                onChange={handleHoraFinChange}
                                placeholder="Seleccione hora"
                                isClearable
                                styles={customStyles}
                                className="basic-select"
                                classNamePrefix="select"
                                value={opcionesHora.find(option => option.value === formData.hora_fin_agenda) || null}
                                required
                            />
                        </div>
                    </div>
                </div>

                {/* Ubicación (ocupa todo el ancho) */}
                <div className="row mb-3">
                    <div className="col-12">
                        <label className="form-label">Ubicación</label>
                        <input
                            type="text"
                            className="form-control"
                            name="ubicacion_agenda"
                            value={formData.ubicacion_agenda}
                            onChange={handleInputChange}
                            placeholder="Ingrese la ubicación del evento"
                        />
                    </div>
                </div>

                {/* Descripción (ocupa todo el ancho) */}
                <div className="row mb-3">
                    <div className="col-12">
                        <label className="form-label">Descripción</label>
                        <textarea
                            className="form-control"
                            name="descripcion_agenda"
                            value={formData.descripcion_agenda}
                            onChange={handleInputChange}
                            rows="3"
                        />
                    </div>
                </div>

                {/* Empresa y Cliente */}
                <div className="row mb-3">
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Empresa</label>
                        <Select
                            options={empresasOpciones}
                            onChange={handleEmpresaChange}
                            placeholder="Buscar y seleccionar empresa"
                            isClearable
                            styles={customStyles}
                            className="basic-select"
                            classNamePrefix="select"
                            value={empresasOpciones.find(option => option.value === formData.id_empresa) || null}
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Cliente</label>
                        <Select
                            options={clientesOpciones}
                            onChange={handleClienteChange}
                            placeholder="Buscar y seleccionar cliente"
                            isClearable
                            isDisabled={!formData.id_empresa}
                            styles={customStyles}
                            className="basic-select"
                            classNamePrefix="select"
                            value={clientesOpciones.find(option => option.value === formData.id_cliente) || null}
                        />
                    </div>
                </div>

                {/* Botones de acción */}
                <div className="d-flex mt-4">
                    <button type="submit" className="btn btn-primary">
                        Actualizar Agenda
                    </button>
                    <button type="button" className="btn btn-secondary ms-2" onClick={() => navigate('/agenda/personal')}>
                        Cancelar
                    </button>
                </div>
            </form>
        </>


    );
};
